/**
 * Variante d'institution (customer-service `InstitutionVariant`), orthogonale
 * au type financier, comme le variant des entreprises :
 *   - financial             : institution financière classique
 *   - support_program       : programme d'appui (ONG, projet, bailleur) qui suit des AVEC
 *   - financial_and_program : les deux, l'utilisateur choisit son espace
 * Les champs propres au programme partent dans `programDetails`.
 */
export type InstitutionProfile = 'financial' | 'support_program' | 'financial_and_program';

const PROFILES: readonly InstitutionProfile[] = ['financial', 'support_program', 'financial_and_program'];

export interface SupportProgramDetails {
  organisationPorteuse: string;
  bailleurs: string[];
  zonesIntervention: string[];
  dateDebut: string;
  dateFin: string;
}

export const EMPTY_SUPPORT_PROGRAM: SupportProgramDetails = {
  organisationPorteuse: '',
  bailleurs: [],
  zonesIntervention: [],
  dateDebut: '',
  dateFin: '',
};

export const INSTITUTION_PROFILE_OPTIONS: { value: InstitutionProfile; label: string }[] = [
  { value: 'financial', label: 'Institution financière' },
  { value: 'support_program', label: "Programme d'appui" },
  { value: 'financial_and_program', label: 'Les deux' },
];

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object';

/** Variante connue de l'institution, 'financial' pour une rangée ancienne. */
export function profileOf(institution: unknown): InstitutionProfile {
  const variant = isObject(institution) ? institution.variant : undefined;
  const raw = typeof variant === 'string' ? variant.trim().toLowerCase() : '';
  return (PROFILES as readonly string[]).includes(raw) ? (raw as InstitutionProfile) : 'financial';
}

export const isProgramProfile = (p: InstitutionProfile) =>
  p === 'support_program' || p === 'financial_and_program';
export const hasFinancialActivity = (p: InstitutionProfile) =>
  p === 'financial' || p === 'financial_and_program';

export function readSupportProgram(raw: unknown): SupportProgramDetails {
  const details = isObject(raw) ? raw.programDetails : undefined;
  const src: Record<string, unknown> = isObject(details) ? details : {};
  const dateOnly = (v: unknown) => (typeof v === 'string' ? v.slice(0, 10) : '');
  return {
    organisationPorteuse: typeof src.organisationPorteuse === 'string' ? src.organisationPorteuse : '',
    bailleurs: Array.isArray(src.bailleurs) ? (src.bailleurs as string[]) : [],
    zonesIntervention: Array.isArray(src.zonesIntervention) ? (src.zonesIntervention as string[]) : [],
    dateDebut: dateOnly(src.dateDebut),
    dateFin: dateOnly(src.dateFin),
  };
}

/**
 * Corps envoyé : la variante toujours (repasser en « financière » doit
 * s'enregistrer), le détail du programme sans chaîne vide (le serveur refuse
 * '' pour une date).
 */
export function buildSupportProgramPayload(
  profile: InstitutionProfile,
  details: SupportProgramDetails,
): { variant: InstitutionProfile; programDetails?: Partial<SupportProgramDetails> } {
  if (!isProgramProfile(profile)) return { variant: profile };
  const text = (v: string) => (v && v.trim() !== '' ? v.trim() : undefined);
  const list = (v: string[]) => v.map((s) => s.trim()).filter(Boolean);
  const programDetails: Partial<SupportProgramDetails> = {
    organisationPorteuse: text(details.organisationPorteuse),
    bailleurs: list(details.bailleurs),
    zonesIntervention: list(details.zonesIntervention),
    dateDebut: text(details.dateDebut),
    dateFin: text(details.dateFin),
  };
  (Object.keys(programDetails) as (keyof SupportProgramDetails)[]).forEach(
    (k) => programDetails[k] === undefined && delete programDetails[k],
  );
  return { variant: profile, programDetails };
}

/** Période cohérente : la fin ne précède pas le début. */
export function supportProgramErrors(profile: InstitutionProfile, details: SupportProgramDetails): string[] {
  if (!isProgramProfile(profile)) return [];
  const errors: string[] = [];
  if (details.dateDebut && details.dateFin && details.dateFin < details.dateDebut) {
    errors.push('La fin du programme ne peut pas précéder son début');
  }
  return errors;
}

/** Champs propres à l'activité financière, retirés pour un programme d'appui seul. */
export const BANKING_ONLY_FIELDS = [
  'typeInstitution',
  'sousCategorie',
  'autoritéSupervision',
  'numeroAgrement',
  'dateAgrement',
  'validiteAgrement',
  'numeroRCCM',
  'numeroNIF',
  'activitesAutorisees',
  'capitalSocialMinimum',
  'capitalSocialActuel',
  'fondsPropresMontant',
  'totalBilan',
  'segmentClientelePrincipal',
  'nombreClientsActifs',
  'portefeuilleCredit',
  'depotsCollectes',
  'servicesCredit',
  'servicesInvestissement',
  'servicesGarantie',
  'servicesTransactionnels',
  'servicesConseil',
] as const;
